import {
    Drawer,
    Box,
    Stack,
    Tooltip,
    Button,
    IconButton,
    Typography,
    Chip,
} from '@mui/material'
import {
    NotificationsActive as NotificationsActiveIcon,
    NotificationsOff as NotificationsOffIcon,
    Close as CloseIcon,
    DoneAll as DoneAllIcon,
    NotificationsNoneOutlined as EmptyBellIcon,
} from '@mui/icons-material'
import UserNotifications, {
    isExcludedNotification,
} from '../UserNotifications'
import { editUser, setNotificationsRead } from '../../apiRoutes'
import { useEffect, useReducer } from 'react'

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <Typography
        sx={{
            px: 1,
            mb: 0.5,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'text.disabled',
        }}
    >
        {children}
    </Typography>
)

const NavMenuNotificationsModal = ({
    open,
    onClose,
    userNotifications,
    handleDeleteNotification,
    handleMarkAsRead,
    disableNotifications,
    notificationsEnabled,
    setUserNotifications,
    user,
    setToastMessage,
}: any) => {
    const defaultState = {
        notifications: userNotifications,
        readNotifications: [],
        unreadNotification: [],
    }
    const reducer = (oldState: any, newState: any) => ({
        ...oldState,
        ...newState,
    })
    const [state, setState] = useReducer(reducer, defaultState)

    const { readNotifications, unreadNotification = [] } = state

    const handleToggleRead = async (notificationId: string) => {
        try {
            const target = userNotifications?.find(
                (n: any) => n._id === notificationId
            )
            const nextRead = !target?.read
            const updatedNotifications = userNotifications?.map((n: any) =>
                n._id === notificationId ? { ...n, read: nextRead } : n
            )
            // Read state is persisted on the notification doc so it survives a
            // refresh (the User's userNotifications array only holds ids).
            await setNotificationsRead({
                ids: [notificationId],
                read: nextRead,
                updatedBy: user?.userEmail,
            })
            setUserNotifications(updatedNotifications)
        } catch (err) {
            console.error(err)
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleMarkAllRead = async () => {
        try {
            const unreadIds = userNotifications
                ?.filter((n: any) => !n.read)
                .map((n: any) => n._id)
            const updateNotifications = userNotifications?.map((n: any) => ({
                ...n,
                read: true,
            }))
            if (unreadIds?.length) {
                await setNotificationsRead({
                    ids: unreadIds,
                    read: true,
                    updatedBy: user?.userEmail,
                })
            }
            setUserNotifications(updateNotifications)
        } catch (err) {
            console.error(err)
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleDeleteAllRead = async () => {
        try {
            const keep = unreadNotification?.map(({ _id }: any) => ({ _id }))
            await editUser({ id: user._id, userNotifications: keep })
            setUserNotifications(unreadNotification)
        } catch (err) {
            console.error(err)
            setToastMessage(`${err}`, 'error')
        }
    }

    useEffect(() => {
        if (userNotifications) {
            // Exclude r / n readings so the New/Earlier counts match the cards.
            const visible = userNotifications.filter(
                (n: any) => !isExcludedNotification(n)
            )
            setState({
                readNotifications: visible.filter((n: any) => n.read),
                unreadNotification: visible.filter((n: any) => !n.read),
            })
        }
    }, [userNotifications])

    const total =
        (unreadNotification?.length || 0) + (readNotifications?.length || 0)
    const unreadCount = unreadNotification?.length || 0
    const readCount = readNotifications?.length || 0

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 420 },
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    px: 2.5,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Notifications
                </Typography>
                {unreadCount > 0 && (
                    <Chip
                        label={unreadCount}
                        size="small"
                        sx={{
                            height: 18,
                            fontSize: 10,
                            fontWeight: 700,
                            bgcolor: 'primary.main',
                            color: '#221F1F',
                            '& .MuiChip-label': { px: 0.75 },
                        }}
                    />
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip
                    title={
                        notificationsEnabled
                            ? 'Mute notifications'
                            : 'Unmute notifications'
                    }
                >
                    <IconButton
                        size="small"
                        onClick={disableNotifications}
                        sx={{ color: 'text.secondary' }}
                    >
                        {notificationsEnabled ? (
                            <NotificationsActiveIcon sx={{ fontSize: 20 }} />
                        ) : (
                            <NotificationsOffIcon sx={{ fontSize: 20 }} />
                        )}
                    </IconButton>
                </Tooltip>
                <Tooltip title="Close">
                    <IconButton
                        size="small"
                        onClick={onClose}
                        sx={{ color: 'text.secondary' }}
                    >
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Actions */}
            {total > 0 && (
                <Box
                    sx={{
                        px: 1.5,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Button
                        size="small"
                        startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
                        onClick={handleMarkAllRead}
                        disabled={unreadCount === 0}
                        sx={{
                            textTransform: 'none',
                            fontSize: 12,
                            color: 'primary.main',
                        }}
                    >
                        Mark all read
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        size="small"
                        onClick={handleDeleteAllRead}
                        disabled={readCount === 0}
                        sx={{
                            textTransform: 'none',
                            fontSize: 12,
                            color: 'text.secondary',
                        }}
                    >
                        Clear read
                    </Button>
                </Box>
            )}

            {/* Body */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1.5 }}>
                {total === 0 ? (
                    <Stack
                        alignItems="center"
                        spacing={1.25}
                        sx={{ mt: 10, color: 'text.disabled' }}
                    >
                        <EmptyBellIcon sx={{ fontSize: 46 }} />
                        <Typography sx={{ fontSize: 13 }}>
                            You're all caught up
                        </Typography>
                    </Stack>
                ) : (
                    <>
                        {unreadCount > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <SectionLabel>New</SectionLabel>
                                <UserNotifications
                                    userNotifications={unreadNotification}
                                    handleDeleteNotification={
                                        handleDeleteNotification
                                    }
                                    handleMarkAsRead={handleToggleRead}
                                />
                            </Box>
                        )}
                        {readCount > 0 && (
                            <Box>
                                <SectionLabel>Earlier</SectionLabel>
                                <UserNotifications
                                    userNotifications={readNotifications}
                                    handleDeleteNotification={
                                        handleDeleteNotification
                                    }
                                    handleMarkAsRead={handleToggleRead}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Drawer>
    )
}

export default NavMenuNotificationsModal
